import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { DisciplineScheduleEffectiveMode } from './enums'
import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'

interface EffectiveModeOption {
  mode: DisciplineScheduleEffectiveMode
  text: string
}

interface ModalDisciplineScheduleEffectiveDateReturn {
  modeOptions: EffectiveModeOption[]
  refMode: ComputedRef<DisciplineScheduleEffectiveMode>
  refIsNow: ComputedRef<boolean>
  refEffectiveDate: ComputedRef<string>
  refMinDate: ComputedRef<string>
  refIsDisabled: ComputedRef<boolean>
  setMode: (mode: DisciplineScheduleEffectiveMode) => void
  setEffectiveDate: (value: string) => void
  close: () => void
  back: () => void
  confirm: () => void
}

const MODE_OPTIONS: EffectiveModeOption[] = [
  { mode: DisciplineScheduleEffectiveMode.Now, text: 'Now' },
  { mode: DisciplineScheduleEffectiveMode.Specify, text: 'Specify the date' },
]

/** When the changed schedule takes over: now, or from the start of a customer day (the API takes a date only). */
export const useModalDisciplineScheduleEffectiveDate = (): ModalDisciplineScheduleEffectiveDateReturn => {
  const store = useDisciplineScheduleStore()
  const { customerNow, close, back } = useDisciplineScheduleContext()

  const refMode = computed<DisciplineScheduleEffectiveMode>(() => store.getEffectiveMode)
  const refIsNow = computed<boolean>(() => store.isEffectiveNow)
  const refEffectiveDate = computed<string>(() => store.getEffectiveDate ?? '')
  const refMinDate = computed<string>(() => customerNow().format(DateFormat.DefaultDate))
  const refIsDisabled = computed<boolean>(() => !store.isEffectiveNow && !store.getEffectiveDate)

  const setMode = (mode: DisciplineScheduleEffectiveMode): void => {
    store.setEffectiveMode(mode)
  }

  const setEffectiveDate = (value: string): void => {
    store.setEffectiveDate(value || null)
  }

  return {
    modeOptions: MODE_OPTIONS,
    refMode,
    refIsNow,
    refEffectiveDate,
    refMinDate,
    refIsDisabled,
    setMode,
    setEffectiveDate,
    close,
    back,
    confirm: () => store.nextStep(),
  }
}
